import { useLocation } from 'react-router';
import { TEDEKBokforderVariantEnum } from '../../../../../../globalQueryTypes';
import {
  IFieldValidation,
  IValidateFn,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { getBokforderVariant } from '../Components/BokforderContext/BokforderContextManager';
import { UtanordningType } from '../Queries/_QueryTypes/UtanordningType';
import {
  validBankkontoUtbetalningsatt,
  validPGBGUtbetalingsatt,
} from '../Utils/UtanordningUtils';

const useValidateUtanordningKontering = (): IValidateFn<UtanordningType> => {
  const location = useLocation();

  return ({ currentValues, touched, data }) => {
    const errors: IFieldValidation[] = [];
    const variant = getBokforderVariant(data, location.search);
    const betsatt = currentValues?._Associations?.Utbetalningssatt?.BETSATT;
    const tillgodohavandeValidateBankkonto =
      variant === TEDEKBokforderVariantEnum.UTANORDNINGTILLGODO
        ? betsatt && validBankkontoUtbetalningsatt.includes(betsatt)
        : false;
    const tillgodohavandeValidatePGBG =
      variant === TEDEKBokforderVariantEnum.UTANORDNINGTILLGODO
        ? betsatt && validPGBGUtbetalingsatt.includes(betsatt)
        : false;

    //------------Gemensam validierng oavsett värde på U_UTBETALNINGSKATEGORI------------//

    if (currentValues.U_BELOPP == null) {
      const BeloppError: IFieldValidation = {
        fieldName: 'U_BELOPP',
        type: 'RequiredValueHint',
        showAsHint: !touched.U_BELOPP,
      };

      errors.push(BeloppError);
    }

    if (
      currentValues.U_BETALNINGSMOTTAGARE == null ||
      currentValues.U_BETALNINGSMOTTAGARE.trim() == ''
    ) {
      // Attribut.tsx validerar inte en sträng som endast inhåller mellanslag som ett fel när man har typen RequiredValueHint.
      const betalningsmottagareError: IFieldValidation = {
        fieldName: 'U_BETALNINGSMOTTAGARE',
        type: 'RequiredValueHint',
        showAsHint: !touched.U_BETALNINGSMOTTAGARE, //Blir röd direkt om man inte använder denna
      };

      errors.push(betalningsmottagareError);
    }

    if (currentValues._Associations?.Utbetalningssatt == null) {
      const BeloppError: IFieldValidation = {
        fieldName: 'Utbetalningssatt',
        type: 'RequiredValueHint',
        showAsHint: !touched._Associations?.Utbetalningssatt,
      };

      errors.push(BeloppError);
    }

    //------------U_UTBETALNINGSKATEGORI för bankkonto------------//

    if (
      variant === TEDEKBokforderVariantEnum.UTANORDINGBANKONTO ||
      tillgodohavandeValidateBankkonto
    ) {
      if (
        currentValues.U_CLEARING_BANKKONTONUMMER == null ||
        currentValues.U_CLEARING_BANKKONTONUMMER?.trim() == ''
      ) {
        // Attribut.tsx validerar inte en sträng som endast inhåller mellanslag som ett fel när man har typen RequiredValueHint.
        const betalningsmottagareError: IFieldValidation = {
          fieldName: 'U_CLEARING_BANKKONTONUMMER',
          type: 'RequiredValueHint',
          showAsHint: !touched.U_CLEARING_BANKKONTONUMMER, //Blir röd direkt om man inte använder denna
        };

        errors.push(betalningsmottagareError);
      }

      if (currentValues.U_BANK == null || currentValues.U_BANK.trim() == '') {
        // Attribut.tsx validerar inte en sträng som endast inhåller mellanslag som ett fel när man har typen RequiredValueHint.
        const bankRequiredError: IFieldValidation = {
          fieldName: 'U_BANK',
          type: 'RequiredValueHint',
          showAsHint: !touched.U_BANK, //Blir röd direkt om man inte använder denna
        };

        errors.push(bankRequiredError);
      }

      // Clearing-/bankkonto nummer måste finnas om vi ska validera på värdet
      if (currentValues.U_CLEARING_BANKKONTONUMMER) {
        const bankKontoArray =
          currentValues.U_CLEARING_BANKKONTONUMMER?.split('-');

        if (
          !currentValues.U_CLEARING_BANKKONTONUMMER?.includes('-') ||
          bankKontoArray.length > 2
        ) {
          const error: IFieldValidation = {
            fieldName: 'U_CLEARING_BANKKONTONUMMER',
            type: 'Error',
            showAsHint: !touched.U_CLEARING_BANKKONTONUMMER,
            message:
              'Kontonumret ska innehålla clearingnummer och kontonnummer separerat med ett bindestreck',
          };

          errors.push(error);
        } else {
          if (isNaN(+bankKontoArray[0]) || isNaN(+bankKontoArray[1])) {
            const error: IFieldValidation = {
              fieldName: 'U_CLEARING_BANKKONTONUMMER',
              type: 'Error',
              showAsHint: !touched.U_CLEARING_BANKKONTONUMMER,
              message: `Clearing- och bankkontonummer får endast innehålla siffror`,
            };

            errors.push(error);
          } else {
            if (bankKontoArray[0].length > 5) {
              const error: IFieldValidation = {
                fieldName: 'U_CLEARING_BANKKONTONUMMER',
                type: 'Error',
                showAsHint: !touched.U_CLEARING_BANKKONTONUMMER,
                message: `Clearingnummer (${
                  bankKontoArray[0] ?? ''
                }) får vara maximalt 5 tecken`,
              };

              errors.push(error);
            }

            if (bankKontoArray[0].charAt(0) === '8') {
              if (
                bankKontoArray[0].length !== 5 &&
                bankKontoArray[0].length !== 4
              ) {
                const error: IFieldValidation = {
                  fieldName: 'U_CLEARING_BANKKONTONUMMER',
                  type: 'Error',
                  showAsHint: !touched.U_CLEARING_BANKKONTONUMMER,
                  message: `Clearingnummer som börjar på 8 ska vara 4 eller 5 tecken`,
                };

                errors.push(error);
              }
            } else if (bankKontoArray[0].charAt(0) !== '8') {
              if (bankKontoArray[0].length !== 4) {
                const error: IFieldValidation = {
                  fieldName: 'U_CLEARING_BANKKONTONUMMER',
                  type: 'Error',
                  showAsHint: !touched.U_CLEARING_BANKKONTONUMMER,
                  message: `Clearingnummer som inte börjar på 8 ska vara 4 tecken`,
                };

                errors.push(error);
              }
            }

            if (bankKontoArray[1].length > 11) {
              const error: IFieldValidation = {
                fieldName: 'U_CLEARING_BANKKONTONUMMER',
                type: 'Error',
                showAsHint: !touched.U_CLEARING_BANKKONTONUMMER,
                message: `Kontonummer (${
                  bankKontoArray[1] ?? ''
                }) får vara maximalt 11 tecken`,
              };

              errors.push(error);
            }
          }
        }
      }
    }

    //------------U_UTBETALNINGSKATEGORI för PGBG------------//

    if (
      variant === TEDEKBokforderVariantEnum.UTANORDINGPGBG ||
      tillgodohavandeValidatePGBG
    ) {
      if (
        currentValues.U_BANK_PLUSGIRONUMMER == null ||
        currentValues.U_BANK_PLUSGIRONUMMER?.trim() == ''
      ) {
        // Attribut.tsx validerar inte en sträng som endast inhåller mellanslag som ett fel när man har typen RequiredValueHint.
        const betalningsmottagareError: IFieldValidation = {
          fieldName: 'U_BANK_PLUSGIRONUMMER',
          type: 'RequiredValueHint',
          showAsHint: !touched.U_BANK_PLUSGIRONUMMER, //Blir röd direkt om man inte använder denna
        };

        errors.push(betalningsmottagareError);
      } else {
        if (isNaN(+currentValues.U_BANK_PLUSGIRONUMMER)) {
          const error: IFieldValidation = {
            fieldName: 'U_BANK_PLUSGIRONUMMER',
            type: 'Error',
            showAsHint: !touched.U_BANK_PLUSGIRONUMMER,
            message: `Bank-/Plus-gironummer får endast innehålla siffror`,
          };

          errors.push(error);
        }
      }
    }

    return errors;
  };
};

export default useValidateUtanordningKontering;
